import type { 
  Goal, 
  Plan, 
  Workout, 
  MobilityRoutine, 
  RestDay, 
  HierarchicalData, 
  PlanWithWorkoutsAndMobility
} from '../types';

import type { 
  ActivityData
} from '../types/DataTypes';

import { WeeklyPlanGenerator } from './weeklyPlanGenerator';
import { storage } from './storage';
import { normalizeBackendGoal } from './goalNormalizer';

// ========================================
// REAL BACKEND INTEGRATION FOR MYPATH
// ========================================

import { apiCall, isNoCredentialResponse } from './api';

/**
 * Authenticated request for this service.
 *
 * The credential is attached by `apiCall` -> `fetchWithAuth`, which proactively
 * refreshes a near-expired JWT, retries exactly once on a server 401, and NEVER fires
 * a request when there is no credential to send. The hand-rolled bearer-token header
 * this replaces could do none of those things.
 *
 * The throw is deliberate and load-bearing, not a leftover. The helper this replaces
 * threw `Authentication required` whenever the token was absent, and every caller below
 * is written around that behaviour — `getAllData` most of all, where the throw is what
 * reaches the catch that returns the last-known-good cache. Without it the flow would
 * fall through and overwrite `dataCache` with empty arrays, which is what a user sees
 * as "my workouts disappeared". `apiCall` reports the same condition as a marked
 * synthetic 401 rather than a throw, so it is translated back here and every caller's
 * error/fallback semantics stay exactly as they were.
 *
 * `Content-Type: application/json` was attached to every request by the old helper, so
 * it stays the default here (callers may still override it) and no request's headers
 * change as part of this migration.
 */
async function authedFetch(path: string, init: RequestInit = {}): Promise<Response> {
  const res = await apiCall(path, {
    ...init,
    headers: {
      'Content-Type': 'application/json',
      ...((init.headers as Record<string, string> | undefined) ?? {}),
    },
  });
  if (isNoCredentialResponse(res)) {
    throw new Error('Authentication required');
  }
  return res;
}

// Cache for performance
let dataCache: {
  goals: Goal[];
  plans: Plan[];
  workouts: Workout[];
  mobilityRoutines: MobilityRoutine[];
  restDays: RestDay[];
  activities?: ActivityData[];
  userHeartRateZones?: UserHeartRateZones; // 🆕 PHASE 3: User's HR zones
  lastFetch: number;
  userId: string | null;
} = {
  goals: [],
  plans: [],
  workouts: [],
  mobilityRoutines: [],
  restDays: [],
  activities: [],
  userHeartRateZones: undefined,
  lastFetch: 0,
  userId: null
};

const CACHE_DURATION = 5 * 60 * 1000; // 5 minutes

export class DataService {
  /**
   * 📦 Get cached data synchronously (for render functions)
   */
  static getCachedData(): HierarchicalData | null {
    if (dataCache.goals.length === 0) return null;
    
    return {
      goals: dataCache.goals,
      plans: dataCache.plans,
      workouts: dataCache.workouts,
      mobilityRoutines: dataCache.mobilityRoutines,
      restDays: dataCache.restDays
    };
  }

  /**
   * 🗑️ Clear cached data (force refresh on next fetch)
   * 
   * Use cases:
   * - After recalibration notifications
   * - After plan updates
   * - User-initiated refresh
   */
  static clearCache(): void {
    console.log('🗑️ DataService: Clearing cache');
    dataCache = {
      goals: [],
      plans: [],
      workouts: [],
      mobilityRoutines: [],
      restDays: [],
      activities: [],
      userHeartRateZones: undefined,
      lastFetch: 0,
      userId: null
    };
  }

  /**
   * 🔄 Fetch all data from backend APIs
   */
  static async getAllData(): Promise<HierarchicalData> {
    const now = Date.now();

    // Invalidate cache if user has changed (prevents cross-user data leakage)
    const currentUserId = await storage.getItem('user_id');
    if (dataCache.userId !== currentUserId) {
      DataService.clearCache();
      dataCache.userId = currentUserId;
    }

    // Return cached data if recent
    if (now - dataCache.lastFetch < CACHE_DURATION && dataCache.goals.length > 0) {
      console.log('📦 DataService: Returning cached data', {
        goals: dataCache.goals.length,
        plans: dataCache.plans.length,
        workouts: dataCache.workouts.length
      });
      return {
        goals: dataCache.goals,
        plans: dataCache.plans,
        workouts: dataCache.workouts,
        mobilityRoutines: dataCache.mobilityRoutines,
        restDays: dataCache.restDays
      };
    }

    console.log('🔄 DataService: Fetching fresh data from APIs...');

    try {
      // 🔧 BUG FIX (Nov 18, 2025): Fixed API endpoints
      // - Changed /plans/history/summary → /api/plans (correct endpoint)
      // - Added /api/workouts to fetch workouts directly from database
      // - This fixes plans/workouts not showing in Move section
      // 🆕 PHASE 3 (Dec 30, 2025): Added /api/hr-zones to fetch user's heart rate zones
      // 🆕 JAN 2026 PRIORITY 1 & 2: Added /api/mobility/routines and /api/rest-days
      const [goalsRes, plansRes, workoutsRes, mobilityRes, restDaysRes, currentWeekPlan, hrZonesRes] = await Promise.all([
        authedFetch('/api/goals'),
        authedFetch('/api/plans'),
        authedFetch('/api/workouts?limit=200'),
        authedFetch('/api/mobility/routines'),
        authedFetch('/api/rest-days'),
        WeeklyPlanGenerator.getCurrentWeekPlan(),
        authedFetch('/api/hr-zones').catch(() => null) // 🆕 PHASE 3: Optional, won't break if fails
      ]);

      console.log('📡 API Responses:', {
        goalsStatus: goalsRes.status,
        plansStatus: plansRes.status,
        workoutsStatus: workoutsRes.status,
        mobilityStatus: mobilityRes.status,  // 🆕 JAN 2026 PRIORITY 2
        restDaysStatus: restDaysRes.status,  // 🆕 JAN 2026 PRIORITY 1
        currentWeekPlan: currentWeekPlan ? 'exists' : 'null'
      });

      // Parse goals
      let goals: Goal[] = [];
      if (goalsRes.ok) {
        const goalsData = await goalsRes.json();
        const rawGoals = Array.isArray(goalsData) ? goalsData : goalsData.goals || [];
        goals = rawGoals.map(this.convertBackendGoalToFrontend);
        console.log('✅ Goals parsed:', goals.length);
      } else {
        console.error('❌ Goals API failed:', goalsRes.status);
      }

      // Parse plans
      let plans: Plan[] = [];
      if (plansRes.ok) {
        const plansData = await plansRes.json();
        console.log('📋 Raw plans data:', plansData);
        const rawPlans = Array.isArray(plansData) ? plansData : plansData.plans || [];
        console.log('📋 Raw plans array:', rawPlans.length, 'plans');
        // 🔧 FIX (Nov 2025): Convert backend format to frontend format
        // Backend returns snake_case, frontend expects camelCase + specific fields
        plans = rawPlans.map((rawPlan: any) => WeeklyPlanGenerator.convertBackendPlanToFrontend(rawPlan));
        console.log('✅ Plans parsed:', plans.length);
      } else {
        console.error('❌ Plans API failed:', plansRes.status);
      }

      // Add current week plan if it exists
      if (currentWeekPlan) {
        const existingPlanIndex = plans.findIndex(p => p.id === currentWeekPlan.id);
        if (existingPlanIndex >= 0) {
          plans[existingPlanIndex] = currentWeekPlan;
        } else {
          plans.unshift(currentWeekPlan);
        }
      }

      // 🔧 BUG FIX (Nov 18, 2025): Parse workouts directly from API
      // Previously tried to extract from plan.plan_data.days, but /api/plans doesn't return full plan_data
      // Now fetching workouts directly from /api/workouts endpoint
      console.log('🔍 Parsing workouts from API...');
      let workouts: Workout[] = [];
      let mobilityRoutines: MobilityRoutine[] = [];
      let restDays: RestDay[] = [];

      if (workoutsRes.ok) {
        const workoutsData = await workoutsRes.json();
        const rawWorkouts = workoutsData.workouts || [];
        console.log('💪 Raw workouts from API:', rawWorkouts.length);
        
        // 🔧 FIX (Dec 6, 2025): Map ALL fields from API response
        // Previously was showing placeholder/default because many fields were hardcoded
        workouts = rawWorkouts.map((w: any) => ({
          id: w.id,
          planId: w.plan_id || undefined,
          title: w.title || w.name || 'Workout Session',
          name: w.name,
          type: w.workout_type || w.activity_type || 'strength',
          activityType: w.activity_type || 'strength',
          description: w.description || `A ${w.workout_type || 'workout'} session`,
          duration: w.duration_minutes || 30,
          intensity: w.intensity !== undefined && w.intensity !== null ? parseFloat(w.intensity) : undefined,  // 🎨 DEC 2025: Intensity for icon color mapping
          exercises: Array.isArray(w.exercises) ? w.exercises : [],
          phases: Array.isArray(w.phases) ? w.phases : [],  // For cardio intervals
          mediaAssets: Array.isArray(w.media_assets) ? w.media_assets : [],  // 🆕 Images/videos
          // 🐛 FEB 3, 2026 (Bug #74): Keep as STRING to avoid timezone conversion
          // Backend returns "2026-02-04" → Creating Date object converts to midnight UTC
          // In EST, midnight UTC Feb 4 = 7pm Feb 3 → Card shows Feb 3 (WRONG)
          // Solution: Keep as string, formatCardDateShort will parse at noon UTC
          scheduledDate: (typeof w.scheduledDate === 'string' && w.scheduledDate) ? w.scheduledDate
            : (typeof w.scheduled_date === 'string' && w.scheduled_date) ? w.scheduled_date : undefined,
          status: w.status || 'active',  // Use actual status from API
          difficulty: w.difficulty || 'intermediate',
          equipment: Array.isArray(w.equipment) ? w.equipment : [],
          targetMuscleGroups: Array.isArray(w.target_areas) ? w.target_areas : [],
          workout_data: w.workout_data || {},  // 🆕 JAN 2026: Include full workout_data JSONB
          activity_id: w.activity_id || undefined,  // 🆕 JAN 2026: Link to unified_athletic_data
          estimated_distance: w.estimated_distance || w.distance_km || undefined,  // 🆕 JAN 28, 2026: Include distance from backend calculator
          context: w.context || 'standalone',  // 🆕 FEB 2 (Bug #61): Context for rest_day linking
          timeOfDay: w.time_of_day || undefined,  // 🆕 APR 2026: Time-of-day for multi-workout day ordering
          // 🔧 REACT ERROR #31 FIX (Jan 22, 2026): Validate timestamps are strings
          createdAt: (typeof w.created_at === 'string' && w.created_at) ? new Date(w.created_at) : undefined,
          updatedAt: (typeof w.updated_at === 'string' && w.updated_at) ? new Date(w.updated_at) : new Date()
        }));
        console.log('✅ Workouts parsed:', workouts.length);
      } else {
        console.error('❌ Workouts API failed:', workoutsRes.status);
      }
      
      // 🆕 JAN 2026 PRIORITY 2: Parse mobility routines from API
      if (mobilityRes && mobilityRes.ok) {
        const mobilityData = await mobilityRes.json();
        const rawMobility = Array.isArray(mobilityData) ? mobilityData : [];
        console.log('🧘 Raw mobility from API:', rawMobility.length);
        
        mobilityRoutines = rawMobility.map((m: any) => ({
          id: m.id,
          planId: m.plan_id || undefined,
          workoutId: m.workout_id || undefined,  // 🆕 Parent workout link for component mobility
          title: m.title || 'Mobility Session',
          description: m.description || '',
          // 🔧 JAN 31, 2026: MobilityDetail expects duration as STRING ("20 minutes")
          duration: m.duration_minutes ? `${m.duration_minutes} minutes` : '15 minutes',
          duration_minutes: m.duration_minutes || 15,  // Keep numeric version for ExpandableComponentMobility
          // 🐛 FEB 3, 2026 (Bug #74): Keep as STRING (same timezone issue as workouts)
          scheduledDate: (typeof m.scheduled_date === 'string' && m.scheduled_date) ? m.scheduled_date : undefined,
          status: m.status || 'active',
          modality: m.modality || 'stretch',
          // 🆕 PHASE 11 SUB-SPRINT (APR 19, 2026) — Warmup/Cooldown Faithful Projection.
          // Structural `component_role` is the source of truth when present;
          // title-keyword inference is the dual-read fallback during transition.
          // PR B3 removes the inference branch after Phase E observation.
          type: (() => {
            // 🎯 Structural read first
            if (m.component_role === 'warmup') return 'warmup';
            if (m.component_role === 'cooldown') return 'cooldown';

            const title = (m.title || '').toLowerCase();
            if (title.includes('warmup') || title.includes('warm-up') || title.includes('pre-') || title.includes('activation')) {
              return 'warmup';
            } else if (title.includes('cooldown') || title.includes('cool-down') || title.includes('post-') || title.includes('recovery stretch')) {
              return 'cooldown';
            } else if (title.includes('yoga')) {
              return 'yoga';
            } else if (title.includes('smr') || title.includes('foam roll')) {
              return 'smr';
            } else {
              return 'static';  // Default
            }
          })(),
          // 🆕 JAN 31, 2026: Map poses/exercises with images
          // 🔧 FIX: Map to BOTH 'poses' (MobilityDetail) AND 'exercises' (ExpandableComponentMobility)
          poses: (m.routine_data?.poses || m.routine_data?.exercises || []).map((ex: any) => ({
            ...ex,
            // Map backend image fields to frontend expected format
            images: ex.media_urls || ex.images || [],
            image_url: (ex.media_urls && ex.media_urls[0]) || (ex.images && ex.images[0]) || undefined
          })),
          exercises: (m.routine_data?.poses || m.routine_data?.exercises || []).map((ex: any) => ({
            ...ex,
            // Map backend image fields to frontend expected format
            images: ex.media_urls || ex.images || [],
            image_url: (ex.media_urls && ex.media_urls[0]) || (ex.images && ex.images[0]) || undefined
          })),
          // 🐛 FEB 3, 2026 (Bug #72): DIAGNOSTIC - Log mapping
          _debug_routine_data: m.routine_data ? 'exists' : 'missing',
          _debug_poses_count: (m.routine_data?.poses || []).length,
          _debug_exercises_count: (m.routine_data?.exercises || []).length,
          targetAreas: m.routine_data?.target_areas || [],
          context: m.context || 'standalone',  // 🆕 standalone/component/daily/rest_day
          timeOfDay: m.time_of_day || undefined,  // 🆕 morning/afternoon/evening
          frequency: m.frequency || undefined,  // 🆕 daily/weekly/as_needed
          saveToHistory: m.save_to_history !== false,  // 🆕 Minor activities flag
          isPhysio: m.is_physio || false,
          skipped: m.skipped || false,
          skipReason: m.skip_reason || undefined,
          // 🆕 PHASE 11 SUB-SPRINT: pass structural fields through so downstream
          // classifiers (timer + ExpandableComponentMobility) can read them.
          component_role: (m.component_role === 'warmup' || m.component_role === 'cooldown')
            ? m.component_role
            : null,
          rest_day_id: m.rest_day_id || undefined,
          createdAt: new Date(m.created_at),
          updatedAt: new Date(m.updated_at || m.created_at),
          completedAt: m.completed_at ? new Date(m.completed_at) : undefined
        }));
        console.log('✅ Mobility routines parsed:', mobilityRoutines.length);
      } else {
        console.error('❌ Mobility API failed:', mobilityRes?.status || 'no response');
      }
      
      // 🆕 JAN 2026 PRIORITY 1: Parse rest days from API
      if (restDaysRes && restDaysRes.ok) {
        const restDaysData = await restDaysRes.json();
        const rawRestDays = Array.isArray(restDaysData) ? restDaysData : [];
        console.log('🛌 Raw rest days from API:', rawRestDays.length);
        
        restDays = rawRestDays.map((r: any) => ({
          id: r.id,
          planId: r.plan_id || undefined,
          title: r.title || 'Rest Day',
          description: r.description || 'Complete rest and recovery.',
          // 🐛 FEB 3, 2026 (Bug #74): Keep as STRING (same timezone issue)
          scheduledDate: (typeof r.scheduled_date === 'string' && r.scheduled_date) ? r.scheduled_date : undefined,
          date: (typeof r.scheduled_date === 'string' && r.scheduled_date) ? r.scheduled_date : undefined,
          type: r.type || 'complete',
          status: r.status || 'active',
          activities: r.activities || [],
          notes: r.notes || undefined,
          createdAt: new Date(r.created_at),
          updatedAt: new Date(r.updated_at || r.created_at),
          completedAt: r.completed_at ? new Date(r.completed_at) : undefined
        }));
        console.log('✅ Rest days parsed:', restDays.length);
      } else {
        console.error('❌ Rest days API failed:', restDaysRes?.status || 'no response');
      }

      plans.forEach(plan => {
        if (plan.plan_data) {
          // 🔧 FIX: Extract from days array (new plan structure)
          if (plan.plan_data.days && Array.isArray(plan.plan_data.days)) {
            plan.plan_data.days.forEach((day: any) => {
              const dayDate = day.date;
              
              // Extract workouts from this day
              if (day.workouts) {
                const dayWorkouts = Array.isArray(day.workouts) ? day.workouts : [day.workouts];
                dayWorkouts.forEach((workout: any) => {
                  workouts.push({
                    id: workout.id || `${plan.id}_workout_${Math.random()}`,
                    planId: plan.id,
                    title: workout.title || workout.name || 'Workout',
                    type: workout.type || workout.activity_type || 'strength',
                    description: workout.description || '',
                    duration: workout.duration || 30,
                    exercises: workout.exercises || [],
                    // 🔧 REACT ERROR #31 FIX (Jan 22, 2026): Validate date
                    scheduledDate: (typeof dayDate === 'string' && dayDate) ? new Date(dayDate) : undefined,
                    status: workout.status || 'active',
                    difficulty: workout.difficulty || 'intermediate',
                    equipment: workout.equipment || [],
                    targetMuscleGroups: workout.target_muscle_groups || [],
                    createdAt: new Date(),
                    updatedAt: new Date()
                  });
                });
              }
              
              // Extract yoga from this day (treat as workouts)
              if (day.yoga) {
                const dayYoga = Array.isArray(day.yoga) ? day.yoga : [day.yoga];
                dayYoga.forEach((yoga: any) => {
                  workouts.push({
                    id: yoga.id || `${plan.id}_yoga_${Math.random()}`,
                    planId: plan.id,
                    title: yoga.title || yoga.name || 'Yoga',
                    type: 'yoga-flow',
                    description: yoga.description || '',
                    duration: yoga.duration || 30,
                    exercises: yoga.exercises || [],
                    // 🔧 REACT ERROR #31 FIX (Jan 22, 2026): Validate date
                    scheduledDate: (typeof dayDate === 'string' && dayDate) ? new Date(dayDate) : undefined,
                    status: yoga.status || 'active',
                    difficulty: yoga.difficulty || 'beginner',
                    equipment: yoga.equipment || [],
                    targetMuscleGroups: yoga.target_muscle_groups || [],
                    createdAt: new Date(),
                    updatedAt: new Date()
                  });
                });
              }
              
              // Extract mobility from this day
              if (day.mobility) {
                const dayMobility = Array.isArray(day.mobility) ? day.mobility : [day.mobility];
                dayMobility.forEach((mobility: any) => {
                  mobilityRoutines.push({
                    id: mobility.id || `${plan.id}_mobility_${Math.random()}`,
                    planId: plan.id,
                    title: mobility.title || mobility.name || 'Mobility',
                    description: mobility.description || '',
                    duration: mobility.duration || 15,
                    type: mobility.type || 'static',
                    exercises: mobility.exercises || [],
                    // 🔧 REACT ERROR #31 FIX (Jan 22, 2026): Validate date
                    scheduledDate: (typeof dayDate === 'string' && dayDate) ? new Date(dayDate) : undefined,
                    status: mobility.status || 'active',
                    targetAreas: mobility.target_areas || [],
                    difficulty: mobility.difficulty || 'beginner',
                    createdAt: new Date(),
                    updatedAt: new Date()
                  });
                });
              }
              
              // Extract rest days
              if (day.is_rest || day.rest) {
                restDays.push({
                  id: `${plan.id}_rest_${dayDate}`,
                  planId: plan.id,
                  date: dayDate ? new Date(dayDate) : new Date(),
                  type: 'complete_rest',
                  activities: [],
                  description: 'Rest day',
                  status: 'active',
                  // 🔧 REACT ERROR #31 FIX (Jan 22, 2026): Validate date
                  scheduledDate: (typeof dayDate === 'string' && dayDate) ? new Date(dayDate) : undefined,
                  createdAt: undefined, // Let backend handle timestamps
                  updatedAt: undefined
                });
              }
            });
          }
          
          // 🆕 PHASE 5: Extract rest_days from new structure (plan_data.rest_days array)
          if (plan.plan_data.rest_days && Array.isArray(plan.plan_data.rest_days)) {
            plan.plan_data.rest_days.forEach((restDay: any) => {
              restDays.push({
                id: restDay.id || `${plan.id}_rest_${restDay.scheduled_date}`,
                planId: plan.id,
                date: restDay.scheduled_date ? new Date(restDay.scheduled_date) : new Date(),
                type: restDay.type || 'complete',
                activities: restDay.optional_mobility ? [{
                  title: restDay.optional_mobility.title,
                  duration: restDay.optional_mobility.duration_minutes,
                  exercises: restDay.optional_mobility.exercises || []
                }] : [],
                description: restDay.notes || 'Rest day',
                status: 'active',
                // 🔧 REACT ERROR #31 FIX (Jan 22, 2026): Validate date
                scheduledDate: (typeof restDay.scheduled_date === 'string' && restDay.scheduled_date) ? new Date(restDay.scheduled_date) : undefined,
                createdAt: undefined, // Let backend handle timestamps
                updatedAt: undefined
              });
            });
          }
          
          // ⚠️ FALLBACK: Legacy flat structure (for backwards compatibility)
          else {
            // Extract workouts from plan data (old structure)
            if (plan.plan_data.workouts) {
              const planWorkouts = Array.isArray(plan.plan_data.workouts) 
                ? plan.plan_data.workouts 
                : Object.values(plan.plan_data.workouts);
              
              planWorkouts.forEach((workout: any) => {
                workouts.push({
                  id: workout.id || `${plan.id}_workout_${Math.random()}`,
                  planId: plan.id,
                  title: workout.title || workout.name || 'Workout',
                  type: workout.type || 'strength',
                  description: workout.description || '',
                  duration: workout.duration || 30,
                  exercises: workout.exercises || [],
                  // 🔧 REACT ERROR #31 FIX (Jan 22, 2026): Validate date
                  scheduledDate: (typeof workout.scheduled_date === 'string' && workout.scheduled_date) ? new Date(workout.scheduled_date) : undefined,
                  status: workout.status || 'active',
                  difficulty: workout.difficulty || 'intermediate',
                  equipment: workout.equipment || [],
                  targetMuscleGroups: workout.target_muscle_groups || [],
                  createdAt: new Date(),
                  updatedAt: new Date()
                });
              });
            }

            // Extract mobility routines from plan data (old structure)
            if (plan.plan_data.mobility) {
              const planMobility = Array.isArray(plan.plan_data.mobility)
                ? plan.plan_data.mobility
                : Object.values(plan.plan_data.mobility);

              planMobility.forEach((mobility: any) => {
                mobilityRoutines.push({
                  id: mobility.id || `${plan.id}_mobility_${Math.random()}`,
                  planId: plan.id,
                  title: mobility.title || mobility.name || 'Mobility',
                  description: mobility.description || '',
                  duration: mobility.duration || 15,
                  type: mobility.type || 'static',
                  exercises: mobility.exercises || [],
                  // 🔧 REACT ERROR #31 FIX (Jan 22, 2026): Validate date
                  scheduledDate: (typeof mobility.scheduled_date === 'string' && mobility.scheduled_date) ? new Date(mobility.scheduled_date) : undefined,
                  status: mobility.status || 'active',
                  targetAreas: mobility.target_areas || [],
                  difficulty: mobility.difficulty || 'beginner',
                  createdAt: new Date(),
                  updatedAt: new Date()
                });
              });
            }

            // Extract rest days from plan data (old structure)
            if (plan.plan_data.rest_days) {
              const planRestDays = Array.isArray(plan.plan_data.rest_days)
                ? plan.plan_data.rest_days
                : Object.values(plan.plan_data.rest_days);

              planRestDays.forEach((restDay: any) => {
                restDays.push({
                  id: restDay.id || `${plan.id}_rest_${Math.random()}`,
                  planId: plan.id,
                  date: restDay.date ? new Date(restDay.date) : new Date(),
                  type: restDay.type || 'complete_rest',
                  activities: restDay.activities || [],
                  description: restDay.description || 'Rest day',
                  status: restDay.status || 'active',
                  // 🔧 REACT ERROR #31 FIX (Jan 22, 2026): Validate date
                  scheduledDate: (typeof restDay.scheduled_date === 'string' && restDay.scheduled_date) ? new Date(restDay.scheduled_date) : undefined,
                  createdAt: undefined, // Let backend handle timestamps
                  updatedAt: undefined
                });
              });
            }
          }
        }
      });

      // 🆕 PHASE 3: Parse HR zones
      let userHeartRateZones: UserHeartRateZones | undefined = undefined;
      if (hrZonesRes && hrZonesRes.ok) {
        try {
          const hrZonesData = await hrZonesRes.json();
          if (hrZonesData && hrZonesData.user_id) {
            userHeartRateZones = {
              userId: hrZonesData.user_id,
              source: hrZonesData.source,
              zone1Min: hrZonesData.zone_1_min,
              zone1Max: hrZonesData.zone_1_max,
              zone2Min: hrZonesData.zone_2_min,
              zone2Max: hrZonesData.zone_2_max,
              zone3Min: hrZonesData.zone_3_min,
              zone3Max: hrZonesData.zone_3_max,
              zone4Min: hrZonesData.zone_4_min,
              zone4Max: hrZonesData.zone_4_max,
              zone5Min: hrZonesData.zone_5_min,
              zone5Max: hrZonesData.zone_5_max,
              // FE-8: model axis — row count comes from zoneCount (3/5/7).
              zone6Min: hrZonesData.zone_6_min,
              zone6Max: hrZonesData.zone_6_max,
              zone7Min: hrZonesData.zone_7_min,
              zone7Max: hrZonesData.zone_7_max,
              zoneCount: hrZonesData.zone_count,
              maxHeartRate: hrZonesData.max_heart_rate,
              restingHeartRate: hrZonesData.resting_heart_rate,
              createdAt: new Date(hrZonesData.created_at),
              updatedAt: new Date(hrZonesData.updated_at)
            };
            console.log('✅ HR zones parsed:', userHeartRateZones.source);
          }
        } catch (e) {
          console.warn('⚠️ Failed to parse HR zones:', e);
        }
      } else {
        console.log('ℹ️ No HR zones available for user (will calculate on backend)');
      }

      console.log('✅ Final data summary:', {
        goals: goals.length,
        plans: plans.length,
        workouts: workouts.length,
        mobilityRoutines: mobilityRoutines.length,
        restDays: restDays.length,
        hrZones: userHeartRateZones ? `${userHeartRateZones.source}` : 'none'
      });

      // 🆕 PHASE 2: Filter out component AND rest_day mobility from main plan view
      // Component mobility (warmups/cooldowns) accessible via parent workout
      // Rest day mobility (yoga, foam rolling) accessible via parent rest day
      const standaloneMobility = mobilityRoutines.filter(m => 
        m.context !== 'component' && m.context !== 'rest_day'
      );
      const componentMobility = mobilityRoutines.filter(m => m.context === 'component');
      const restDayMobility = mobilityRoutines.filter(m => m.context === 'rest_day');
      
      // 🆕 FEB 2, 2026 (Bug #61): Also filter out rest_day WORKOUTS from plan view
      // Rest day workouts (recovery walks, easy swims) accessible via parent rest day
      const standaloneWorkouts = workouts.filter(w =>
        w.context !== 'rest_day'
      );
      const restDayWorkouts = workouts.filter(w => w.context === 'rest_day');
      
      console.log('🔍 Mobility filtering:', {
        total: mobilityRoutines.length,
        standalone: standaloneMobility.length,
        component: componentMobility.length,
        restDay: restDayMobility.length  // 🆕 JAN 31: Track rest day activities
      });
      
      console.log('🔍 Workout filtering:', {
        total: workouts.length,
        standalone: standaloneWorkouts.length,
        restDay: restDayWorkouts.length  // 🆕 FEB 2 (Bug #61): Track rest day workouts
      });

      // Update cache (store ALL workouts and mobility for detail views)
      // 🆕 FEB 2, 2026 (Bug #61): Cache includes rest_day context for both workouts and mobility
      dataCache = {
        goals,
        plans,
        workouts, // Store ALL workouts (including rest_day context)
        mobilityRoutines, // Store ALL mobility (including component and rest_day)
        restDays,
        userHeartRateZones, // 🆕 PHASE 3: Include HR zones
        lastFetch: now
      };

      console.log('💾 Data cached successfully');

      return {
        goals,
        plans,
        workouts, // 🆕 FEB 2 (Bug #61): Return ALL workouts (filtering happens at view level)
        mobilityRoutines: standaloneMobility, // 🆕 PHASE 2: Only return standalone mobility
        restDays,
        userHeartRateZones // 🆕 PHASE 3: Include HR zones
      };

    } catch (error) {
      console.error('❌ Failed to fetch data from backend:', error);
      
      // Return cached data if available, otherwise empty
      return {
        goals: dataCache.goals,
        plans: dataCache.plans,
        workouts: dataCache.workouts,
        mobilityRoutines: dataCache.mobilityRoutines,
        restDays: dataCache.restDays
      };
    }
  }

  /**
   * 📅 Get current week's plan from backend
   */
  static async getCurrentWeekPlan(): Promise<Plan | undefined> {
    try {
      const plan = await WeeklyPlanGenerator.getCurrentWeekPlan();
      return plan || undefined;
    } catch (error) {
      console.error('❌ Failed to get current week plan:', error);
      return undefined;
    }
  }

  /**
   * Get all workouts, mobility, and rest days for the current week
   */
  static async getThisWeeksItems(): Promise<{
    workouts: Workout[];
    mobility: MobilityRoutine[];
    restDays: RestDay[];
    weeklyPlan?: Plan;
  }> {
    const currentWeekPlan = await this.getCurrentWeekPlan();
    
    if (!currentWeekPlan) {
      return {
        workouts: [],
        mobility: [],
        restDays: [],
        weeklyPlan: undefined
      };
    }
    
    // Get all data to filter from
    const allData = await this.getAllData();
    
    const weekWorkouts = allData.workouts.filter(workout => 
      workout.planId === currentWeekPlan.id &&
      (workout.status === 'active' || workout.status === 'completed')
    );
    
    const weekMobility = allData.mobilityRoutines.filter(routine => 
      routine.planId === currentWeekPlan.id &&
      (routine.status === 'active' || routine.status === 'completed')
    );

    const weekRestDays = allData.restDays.filter(restDay => 
      restDay.planId === currentWeekPlan.id &&
      (restDay.status === 'active' || restDay.status === 'completed')
    );

    return {
      workouts: weekWorkouts,
      mobility: weekMobility,
      restDays: weekRestDays,
      weeklyPlan: currentWeekPlan
    };
  }

  static getWorkoutById(id: string): Workout | undefined {
    return dataCache.workouts.find(workout => workout.id === id);
  }

  static getMobilityById(id: string): MobilityRoutine | undefined {
    return dataCache.mobilityRoutines.find(routine => routine.id === id);
  }

  static getGoalById(id: string): Goal | undefined {
    return dataCache.goals.find(goal => goal.id === id);
  }

  static getPlanById(id: string): Plan | undefined {
    return dataCache.plans.find(plan => plan.id === id);
  }

  static getPlanWithWorkoutsAndMobility(planId: string): PlanWithWorkoutsAndMobility | undefined {
    const plan = this.getPlanById(planId);
    if (!plan) return undefined;

    // 🔧 MAY 19 2026 (Today-Alignment Sprint):
    // Match the status whitelist used by getTodaysFocus (line ~1266) so Plan
    // Detail's "Today" shows the same items as My Week's "Today's Focus".
    // Backend /api/workouts now filters archived_at + state='skipped' + status
    // IN ('archived','replaced','deleted','cancelled','missed'), but apply the
    // positive whitelist here as defense-in-depth: any future status value
    // writers add defaults to hidden until explicitly whitelisted, which is
    // the safer failure mode. See .claude/audits/2026-05-19/TODAY_ALIGNMENT_SPRINT.md.
    const VALID_WORKOUT_STATUSES = new Set(['planned', 'in_progress', 'completed']);
    const workouts = dataCache.workouts.filter(
      workout =>
        workout.planId === planId &&
        VALID_WORKOUT_STATUSES.has((workout.status as string) ?? '')
    );
    // 🔧 CRITICAL FIX: Filter out component mobility (warmups/cooldowns) from plan view
    // Component mobility should only be accessible via parent workout detail page
    const mobilityRoutines = dataCache.mobilityRoutines.filter(
      routine => routine.planId === planId && routine.context !== 'component'
    );
    const restDays = this.getRestDaysForPlan(planId); // 🆕 PHASE 5: Include rest days
    
    return { ...plan, workouts, mobilityRoutines, restDays };
  }

  static getWorkoutsForPlan(planId: string): Workout[] {
    return dataCache.workouts.filter(workout => workout.planId === planId);
  }

  static getMobilityRoutinesForPlan(planId: string): MobilityRoutine[] {
    // 🔧 CRITICAL FIX: Filter out component mobility (warmups/cooldowns) from plan view
    // Component mobility should only be accessible via parent workout detail page
    return dataCache.mobilityRoutines.filter(
      routine => routine.planId === planId && routine.context !== 'component'
    );
  }

  static getMobilityRoutinesForWorkout(workoutId: string): MobilityRoutine[] {
    return dataCache.mobilityRoutines.filter(routine => routine.workoutId === workoutId);
  }

  static getStandaloneMobilityRoutines(): MobilityRoutine[] {
    return dataCache.mobilityRoutines.filter(routine => !routine.planId && !routine.workoutId);
  }

  static getRestDaysForPlan(planId: string): RestDay[] {
    return dataCache.restDays.filter(restDay => restDay.planId === planId);
  }

  static getRestDayById(id: string): RestDay | undefined {
    return dataCache.restDays.find(restDay => restDay.id === id);
  }

  // ========================================
  // WORKOUT COMPLETION METHODS
  // ========================================

  static analyzeActivityProgress(_workoutId: string): any {
    // Placeholder for activity analysis
    return {
      shouldCelebrate: false,
      progressInsights: [],
      comparisonData: null
    };
  }

  static completeWorkout(workoutId: string, _anecdotalFeedback?: string): { success: boolean; message: string } {
    const workoutIndex = dataCache.workouts.findIndex(w => w.id === workoutId);
    if (workoutIndex === -1) {
      return { success: false, message: 'Workout not found' };
    }

    dataCache.workouts[workoutIndex] = {
      ...dataCache.workouts[workoutIndex],
      status: 'completed',
      completedAt: new Date(),
      updatedAt: new Date()
    };

    return { success: true, message: 'Workout completed successfully!' };
  }

  static async markWorkoutComplete(workoutId: string, notes?: string, sessionData?: any): Promise<{ success: boolean; message: string; activity_id?: string }> {
    try {
      // Call backend API to trigger Mission 22 pipeline
      const response = await authedFetch('/api/workouts/manual-complete', {
        method: 'POST',
        body: JSON.stringify({
          workout_id: workoutId,
          notes: notes || undefined,
          duration_minutes: sessionData?.totalDuration ? Math.round(sessionData.totalDuration / 60) : undefined,
          session_data: sessionData ? {
            duration_seconds: sessionData.totalDuration || 0,
            distance_meters: sessionData.distance || undefined,
            avg_pace_per_km: sessionData.avgPace || undefined,
            gps_data: sessionData.gpsData || undefined,
            splits: sessionData.splits || undefined,
            elevation_gain: sessionData.elevationGain || undefined,
            elevation_loss: sessionData.elevationLoss || undefined,
            avg_heart_rate: sessionData.heartRate?.avg || undefined,
            max_heart_rate: sessionData.heartRate?.max || undefined,
            calories: sessionData.calories || undefined,
            exercises: sessionData.exercises || undefined,
          } : undefined
        })
      });
      
      if (!response.ok) {
        const error = await response.text();
        console.error('❌ Manual completion failed:', error);
        return { success: false, message: `API error: ${response.status}` };
      }
      
      const result = await response.json();
      
      // Update local cache after successful backend call
      const workoutIndex = dataCache.workouts.findIndex(w => w.id === workoutId);
      if (workoutIndex !== -1) {
        dataCache.workouts[workoutIndex] = {
          ...dataCache.workouts[workoutIndex],
          status: 'completed',
          completedAt: new Date(),
          updatedAt: new Date(),
          activity_id: result.activity_id // Link to activity
        };
      }
      
      console.log('✅ Manual completion successful:', result.message);
      return { 
        success: true, 
        message: result.message,
        activity_id: result.activity_id
      };
      
    } catch (error) {
      console.error('❌ Manual completion API call failed:', error);
      // Fallback: Update cache only (legacy behavior)
      return this.completeWorkout(workoutId, notes);
    }
  }

  static markWorkoutIncomplete(workoutId: string): { success: boolean; message: string } {
    const workoutIndex = dataCache.workouts.findIndex(w => w.id === workoutId);
    if (workoutIndex === -1) {
      return { success: false, message: 'Workout not found' };
    }

    dataCache.workouts[workoutIndex] = {
      ...dataCache.workouts[workoutIndex],
      status: 'active',
      completedAt: undefined,
      updatedAt: new Date()
    };

    return { success: true, message: 'Workout marked as incomplete' };
  }

  static skipWorkout(workoutId: string, _reason?: string): { success: boolean; message: string } {
    const workoutIndex = dataCache.workouts.findIndex(w => w.id === workoutId);
    if (workoutIndex === -1) {
      return { success: false, message: 'Workout not found' };
    }

    dataCache.workouts[workoutIndex] = {
      ...dataCache.workouts[workoutIndex],
      status: 'missed',
      updatedAt: new Date()
    };

    return { success: true, message: 'Workout skipped' };
  }

  // 🆕 REST DAY COMPLETION METHODS (Jan 21, 2026)
  // Spec: specs/REST_DAY_CARD_SYSTEM_SPECIFICATION.md
  
  static async markRestDayComplete(restDayId: string): Promise<{ success: boolean; message: string }> {
    const restDayIndex = dataCache.restDays.findIndex(r => r.id === restDayId);
    if (restDayIndex === -1) {
      return { success: false, message: 'Rest day not found' };
    }

    // Optimistic update
    dataCache.restDays[restDayIndex] = {
      ...dataCache.restDays[restDayIndex],
      status: 'completed',
      completedAt: new Date(),
      updatedAt: new Date()
    };

    try {
      // Backend API call with intelligence integration
      const response = await authedFetch(`/api/rest-days/${restDayId}/complete`, {
        method: 'POST'
      });

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}`);
      }

      const data = await response.json();
      
      // Update cache with backend response
      if (data.rest_day) {
        dataCache.restDays[restDayIndex] = this.convertBackendRestDayToFrontend(data.rest_day);
      }

      console.log('✅ REST DAY: Completed with intelligence analysis');
      return { success: true, message: data.message || 'Rest day completed successfully!' };
      
    } catch (error) {
      console.error('❌ REST DAY: Failed to complete:', error);
      // Revert optimistic update
      dataCache.restDays[restDayIndex] = {
        ...dataCache.restDays[restDayIndex],
        status: 'active',
        completedAt: undefined
      };
      return { success: false, message: 'Failed to complete rest day' };
    }
  }

  static async markRestDayIncomplete(restDayId: string): Promise<{ success: boolean; message: string }> {
    const restDayIndex = dataCache.restDays.findIndex(r => r.id === restDayId);
    if (restDayIndex === -1) {
      return { success: false, message: 'Rest day not found' };
    }

    // Optimistic update
    dataCache.restDays[restDayIndex] = {
      ...dataCache.restDays[restDayIndex],
      status: 'active',
      completedAt: undefined,
      updatedAt: new Date()
    };

    try {
      // Backend API call with intelligence re-analysis
      const response = await authedFetch(`/api/rest-days/${restDayId}/incomplete`, {
        method: 'POST'
      });

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}`);
      }

      const data = await response.json();
      
      // Update cache with backend response
      if (data.rest_day) {
        dataCache.restDays[restDayIndex] = this.convertBackendRestDayToFrontend(data.rest_day);
      }

      console.log('✅ REST DAY: Marked incomplete with pattern re-analysis');
      return { success: true, message: data.message || 'Rest day marked as incomplete' };
      
    } catch (error) {
      console.error('❌ REST DAY: Failed to mark incomplete:', error);
      // Revert optimistic update
      dataCache.restDays[restDayIndex] = {
        ...dataCache.restDays[restDayIndex],
        status: 'completed',
        completedAt: new Date()
      };
      return { success: false, message: 'Failed to mark rest day incomplete' };
    }
  }
  
  // Helper to convert backend rest day format to frontend
  private static convertBackendRestDayToFrontend(backendRestDay: any): RestDay {
    return {
      id: backendRestDay.id,
      planId: backendRestDay.plan_id,
      title: backendRestDay.title,
      description: backendRestDay.description,
      // 🔧 REACT ERROR #31 FIX (Jan 22, 2026): Validate date is string
      scheduledDate: (typeof backendRestDay.scheduled_date === 'string' && backendRestDay.scheduled_date) ? new Date(backendRestDay.scheduled_date) : undefined,
      date: (typeof backendRestDay.scheduled_date === 'string' && backendRestDay.scheduled_date) ? new Date(backendRestDay.scheduled_date) : undefined,
      type: backendRestDay.type || 'complete',
      status: backendRestDay.status || 'active',
      activities: backendRestDay.activities || [],
      createdAt: new Date(backendRestDay.created_at),
      updatedAt: new Date(backendRestDay.updated_at),
      completedAt: backendRestDay.completed_at ? new Date(backendRestDay.completed_at) : undefined
    };
  }
  
  static getRestDayWithMobility(restDayId: string): (RestDay & { linkedMobility: MobilityRoutine[] }) | null {
    const restDay = dataCache.restDays.find(r => r.id === restDayId);
    if (!restDay) return null;
    
    // Find mobility routines scheduled on same date
    const dateStr = restDay.scheduledDate ? new Date(restDay.scheduledDate).toISOString().split('T')[0] : '';
    const linkedMobility = dataCache.mobilityRoutines.filter(m => {
      const mobilityDateStr = m.scheduledDate ? new Date(m.scheduledDate).toISOString().split('T')[0] : '';
      return mobilityDateStr === dateStr && m.context === 'rest_day';
    });
    
    return {
      ...restDay,
      linkedMobility
    };
  }

  static getWorkoutState(workout: Workout): string {
    if (workout.status === 'completed') return 'done';
    if (workout.status === 'active') return 'live';
    if (workout.status === 'missed') return 'missed';
    if (workout.status === 'replaced') return 'hidden';
    if (workout.status === 'skipped') return 'hidden';
    return 'archived';
  }

  // ========================================
  // MOBILITY METHODS
  // ========================================

  static getMobilityRoutineById(id: string): MobilityRoutine | undefined {
    return this.getMobilityById(id);
  }

  static markMobilityComplete(mobilityId: string): { success: boolean; message: string } {
    const mobilityIndex = dataCache.mobilityRoutines.findIndex(m => m.id === mobilityId);
    if (mobilityIndex === -1) {
      return { success: false, message: 'Mobility routine not found' };
    }

    dataCache.mobilityRoutines[mobilityIndex] = {
      ...dataCache.mobilityRoutines[mobilityIndex],
      status: 'completed',
      completedAt: new Date(),
      updatedAt: new Date()
    };

    return { success: true, message: 'Mobility routine completed!' };
  }

  static markMobilityIncomplete(mobilityId: string): { success: boolean; message: string } {
    const mobilityIndex = dataCache.mobilityRoutines.findIndex(m => m.id === mobilityId);
    if (mobilityIndex === -1) {
      return { success: false, message: 'Mobility routine not found' };
    }

    dataCache.mobilityRoutines[mobilityIndex] = {
      ...dataCache.mobilityRoutines[mobilityIndex],
      status: 'active',
      completedAt: undefined,
      updatedAt: new Date()
    };

    return { success: true, message: 'Mobility routine marked incomplete' };
  }

  // ========================================
  // GOAL METHODS
  // ========================================

  static getPlansForGoal(goalId: string): Plan[] {
    return dataCache.plans.filter(plan => plan.goalIds.includes(goalId));
  }

  static calculateUnifiedProgress(goal: Goal): { journey: { overallProgress: number; phaseProgress: number } } {
    // 2026-05-05 (Milestone Spider-Web Doctor Mission Thread 7):
    // Replaced the buggy pre-sprint formula:
    //   const currentPhaseProgress = (currentPhaseIndex divided by totalPhases);
    //   phaseProgress = ((completedPhases + currentPhaseProgress) / totalPhases) * 100;
    // That divided currentPhaseIndex by totalPhases TWICE — the bar exceeded
    // 100% at completion and never updated within a phase.
    //
    // New formula per docs/plans/milestone-spider-web-doctor.md Thread 7:
    //   phaseProgress = ((currentPhaseIndex + activePhaseFraction) / totalPhases) * 100
    // where activePhaseFraction = (workouts completed in active phase) /
    //                             (workouts in active phase), bounded [0, 1].
    //
    // No hardcoded short-circuits, no compounding division. Monotonic, never
    // exceeds 100%, updates within a phase as workouts complete.
    const totalPhases = goal.journey?.phases?.length ?? 0;
    const currentPhaseIndex = goal.journey?.currentPhase ?? 0;

    if (totalPhases === 0) {
      return { journey: { overallProgress: 0, phaseProgress: 0 } };
    }

    // All phases complete → 100% (no off-by-one, no >100% overflow)
    if (currentPhaseIndex >= totalPhases) {
      return { journey: { overallProgress: 100, phaseProgress: 100 } };
    }

    // Compute fraction within active phase from workouts in the phase window.
    // Phase window = [phase_started_date, phase_started_date + duration_weeks)
    const activePhase = goal.journey.phases[currentPhaseIndex];
    const phaseStartedRaw = (goal.journey as any)?.phaseStartedDate
      ?? (goal.journey as any)?.phase_started_date;
    const phaseStarted = phaseStartedRaw ? new Date(phaseStartedRaw) : null;
    const durationWeeks = (activePhase as any)?.durationWeeks
      ?? (activePhase as any)?.duration_weeks
      ?? 1;

    let activePhaseFraction = 0;
    if (phaseStarted && !isNaN(phaseStarted.getTime())) {
      const phaseEnd = new Date(phaseStarted);
      phaseEnd.setDate(phaseEnd.getDate() + durationWeeks * 7);

      const workoutsInPhase = this.getWorkoutsForGoalInWindow(
        goal.id,
        phaseStarted,
        phaseEnd,
      );

      if (workoutsInPhase.length > 0) {
        const completed = workoutsInPhase.filter((w) => w.state === 'completed').length;
        activePhaseFraction = Math.min(1, completed / workoutsInPhase.length);
      }
    }

    const phaseProgress = Math.round(
      ((currentPhaseIndex + activePhaseFraction) / totalPhases) * 100,
    );

    // Overall = max of phase progress and plan-completion progress (kept for
    // backwards compatibility with the existing overallProgress contract).
    const plans = this.getPlansForGoal(goal.id);
    let overallProgress = phaseProgress;
    if (plans.length > 0) {
      const completedPlans = plans.filter((p) => p.state === 'completed').length;
      const planProgress = Math.round((completedPlans / plans.length) * 100);
      overallProgress = Math.max(phaseProgress, planProgress);
    }

    return {
      journey: {
        overallProgress,
        phaseProgress,
      },
    };
  }

  /**
   * 2026-05-05 (Thread 7): Helper for within-phase workout fraction.
   * Returns workouts attributed to the goal that fall within [start, end).
   */
  static getWorkoutsForGoalInWindow(
    goalId: string,
    start: Date,
    end: Date,
  ): Workout[] {
    const startMs = start.getTime();
    const endMs = end.getTime();
    return dataCache.workouts.filter((w) => {
      // Goal attribution — workouts can carry goalIds[] or be inferred via
      // plan→goal linkage. Use whichever the cache populates.
      const goalIds = (w as any).goalIds as string[] | undefined;
      if (!goalIds || !goalIds.includes(goalId)) {
        // Fallback: check plan linkage
        const planId = (w as any).planId;
        if (!planId) return false;
        const plan = dataCache.plans.find((p) => p.id === planId);
        if (!plan || !plan.goalIds.includes(goalId)) return false;
      }
      // Date filtering — accept either scheduledDate (Date | string) or scheduled_date
      const dateRaw = (w as any).scheduledDate ?? (w as any).scheduled_date;
      if (!dateRaw) return false;
      const dateMs = (dateRaw instanceof Date ? dateRaw : new Date(dateRaw)).getTime();
      if (isNaN(dateMs)) return false;
      return dateMs >= startMs && dateMs < endMs;
    });
  }

  static getTodaysFocus(): { workouts: Workout[]; mobility: MobilityRoutine[]; restDays: RestDay[] } {
    // 🌍 TIMEZONE FIX (Dec 21, 2025): Use local date in user's timezone, not UTC
    // Problem: User at 9:32 PM on Dec 21 in their timezone, but UTC might be Dec 22
    // Solution: Get today's date in local timezone (YYYY-MM-DD format)
    
    // 🚨 FIX (Jan 21, 2026): Force date to start of day in local timezone
    // Problem: Jan 22 workouts showing when it's Jan 21
    // Solution: Normalize to midnight local time to prevent date drift
    const now = new Date();
    const today = new Date(now.getFullYear(), now.getMonth(), now.getDate(), 0, 0, 0, 0);
    
    // Get local date components (these are in the user's browser timezone)
    const year = today.getFullYear();
    const month = String(today.getMonth() + 1).padStart(2, '0'); // Months are 0-indexed
    const day = String(today.getDate()).padStart(2, '0');
    
    // Create today string in user's LOCAL timezone (not UTC)
    const todayStr = `${year}-${month}-${day}`;
    
    console.log('🔍 getTodaysFocus: Debug info (LOCAL TIMEZONE):', {
      todayStr,
      browserTimezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
      currentTime: today.toLocaleString(),
      totalWorkouts: dataCache.workouts.length,
      workoutsWithDates: dataCache.workouts.map(w => ({
        id: w.id,
        title: w.title,
        scheduledDate: w.scheduledDate,
        scheduledDateType: typeof w.scheduledDate,
        status: w.status
      }))
    });
    
    const workouts = dataCache.workouts.filter(w => {
      if (!w.scheduledDate) {
        console.log('🔍 getTodaysFocus: Workout has no scheduledDate:', w.id, w.title);
        return false;
      }
      
      let workoutDateStr: string;
      if (w.scheduledDate instanceof Date) {
        // Get date components in LOCAL timezone
        const wYear = w.scheduledDate.getFullYear();
        const wMonth = String(w.scheduledDate.getMonth() + 1).padStart(2, '0');
        const wDay = String(w.scheduledDate.getDate()).padStart(2, '0');
        workoutDateStr = `${wYear}-${wMonth}-${wDay}`;
      } else if (typeof w.scheduledDate === 'string') {
        // 🎯 JAN 19, 2026: Extract YYYY-MM-DD directly from string (no timezone conversion)
        // Problem: new Date("2026-01-20") at midnight UTC converts to Jan 19 in EST
        // Solution: Extract date string directly if it's in YYYY-MM-DD format
        if (w.scheduledDate.match(/^\d{4}-\d{2}-\d{2}/)) {
          workoutDateStr = w.scheduledDate.substring(0, 10); // Extract "2026-01-20"
        } else {
          // Fallback for non-standard formats
          const wDate = new Date(w.scheduledDate);
          const wYear = wDate.getFullYear();
          const wMonth = String(wDate.getMonth() + 1).padStart(2, '0');
          const wDay = String(wDate.getDate()).padStart(2, '0');
          workoutDateStr = `${wYear}-${wMonth}-${wDay}`;
        }
      } else {
        console.log('🔍 getTodaysFocus: Workout has invalid scheduledDate type:', w.id, w.title, typeof w.scheduledDate);
        return false;
      }
      
      const isToday = workoutDateStr === todayStr;
      // 🔧 FIX (Dec 20, 2025): Database uses 'planned', 'in_progress', 'completed', 'skipped' - NOT 'active'
      const isValidStatus = (w.status === 'planned' || w.status === 'in_progress' || w.status === 'completed');
      
      // 🚨 SAFETY NET (Jan 21, 2026): Explicitly exclude future dates
      // Problem: Jan 22 workouts appearing when it's Jan 21
      // Defense: Only allow dates <= today (no future dates in "Today's Focus")
      const isFutureDate = workoutDateStr > todayStr;
      
      console.log(`🔍 getTodaysFocus: Workout ${w.title} - scheduledDate (LOCAL): ${workoutDateStr}, todayStr (LOCAL): ${todayStr}, isToday: ${isToday}, isFuture: ${isFutureDate}, status: ${w.status}, isValidStatus: ${isValidStatus}`);
      
      return isToday && !isFutureDate && isValidStatus;
    });
    
    const mobility = dataCache.mobilityRoutines.filter(m => {
      // 🔧 CRITICAL FIX: Filter out component mobility (warmups/cooldowns) from today's view
      // Component mobility should only be accessible via parent workout detail page
      if (m.context === 'component') return false;
      
      if (!m.scheduledDate) return false;
      
      let mobilityDateStr: string;
      if (m.scheduledDate instanceof Date) {
        const mYear = m.scheduledDate.getFullYear();
        const mMonth = String(m.scheduledDate.getMonth() + 1).padStart(2, '0');
        const mDay = String(m.scheduledDate.getDate()).padStart(2, '0');
        mobilityDateStr = `${mYear}-${mMonth}-${mDay}`;
      } else if (typeof m.scheduledDate === 'string') {
        // 🎯 JAN 19, 2026: Extract YYYY-MM-DD directly (no timezone conversion)
        if (m.scheduledDate.match(/^\d{4}-\d{2}-\d{2}/)) {
          mobilityDateStr = m.scheduledDate.substring(0, 10);
        } else {
          const mDate = new Date(m.scheduledDate);
          const mYear = mDate.getFullYear();
          const mMonth = String(mDate.getMonth() + 1).padStart(2, '0');
          const mDay = String(mDate.getDate()).padStart(2, '0');
          mobilityDateStr = `${mYear}-${mMonth}-${mDay}`;
        }
      } else {
        return false;
      }
      
      // 🔧 FIX (Dec 20, 2025): Database uses 'planned', 'in_progress', 'completed', 'skipped' - NOT 'active'
      // 🚨 SAFETY NET (Jan 21, 2026): Explicitly exclude future dates
      const isFutureDate = mobilityDateStr > todayStr;
      return mobilityDateStr === todayStr && !isFutureDate && (m.status === 'planned' || m.status === 'in_progress' || m.status === 'completed');
    });
    
    const restDays = dataCache.restDays.filter(r => {
      if (!r.scheduledDate) return false;
      
      let restDayDateStr: string;
      if (r.scheduledDate instanceof Date) {
        const rYear = r.scheduledDate.getFullYear();
        const rMonth = String(r.scheduledDate.getMonth() + 1).padStart(2, '0');
        const rDay = String(r.scheduledDate.getDate()).padStart(2, '0');
        restDayDateStr = `${rYear}-${rMonth}-${rDay}`;
      } else if (typeof r.scheduledDate === 'string') {
        // 🎯 JAN 19, 2026: Extract YYYY-MM-DD directly (no timezone conversion)
        if (r.scheduledDate.match(/^\d{4}-\d{2}-\d{2}/)) {
          restDayDateStr = r.scheduledDate.substring(0, 10);
        } else {
          const rDate = new Date(r.scheduledDate);
          const rYear = rDate.getFullYear();
          const rMonth = String(rDate.getMonth() + 1).padStart(2, '0');
          const rDay = String(rDate.getDate()).padStart(2, '0');
          restDayDateStr = `${rYear}-${rMonth}-${rDay}`;
        }
      } else {
        return false;
      }
      
      // 🔧 FIX (Dec 20, 2025): Database uses 'planned', 'in_progress', 'completed', 'skipped' - NOT 'active'
      // 🚨 SAFETY NET (Jan 21, 2026): Explicitly exclude future dates
      const isFutureDate = restDayDateStr > todayStr;
      return restDayDateStr === todayStr && !isFutureDate && (r.status === 'planned' || r.status === 'in_progress' || r.status === 'completed');
    });
    
    console.log('🔍 getTodaysFocus: Final results (LOCAL TIMEZONE):', { todayStr, workoutsCount: workouts.length, mobilityCount: mobility.length, restDaysCount: restDays.length });

    return { workouts, mobility, restDays };
  }

  // ========================================
  // ADAPTIVE SCHEDULER SUPPORT METHODS
  // ========================================

  static getWorkout(workoutId: string): Workout | undefined {
    return dataCache.workouts.find(w => w.id === workoutId);
  }

  static getWorkoutsInDateRange(startDate: Date, endDate: Date): Workout[] {
    return dataCache.workouts.filter(w => {
      if (!w.scheduledDate) return false;
      return w.scheduledDate >= startDate && w.scheduledDate <= endDate;
    });
  }

  static updateWorkoutSchedule(workoutId: string, newDate: Date): { success: boolean; message: string } {
    const workoutIndex = dataCache.workouts.findIndex(w => w.id === workoutId);
    if (workoutIndex === -1) {
      return { success: false, message: 'Workout not found' };
    }

    dataCache.workouts[workoutIndex] = {
      ...dataCache.workouts[workoutIndex],
      scheduledDate: newDate,
      status: 'active',
      updatedAt: new Date()
    };

    return { success: true, message: 'Workout rescheduled successfully' };
  }

  static addWorkout(workout: Partial<Workout>): Workout {
    const newWorkout: Workout = {
      id: workout.id || `workout_${Date.now()}`,
      planId: workout.planId,
      weeklyPlanId: workout.weeklyPlanId,
      title: workout.title || 'New Workout',
      description: workout.description || '',
      duration: workout.duration || '30 min',
      type: workout.type || 'cardio',
      status: workout.status || 'active',
      scheduledDate: workout.scheduledDate,
      completedAt: workout.completedAt,
      createdAt: workout.createdAt || new Date(),
      updatedAt: workout.updatedAt || new Date()
    };

    dataCache.workouts.push(newWorkout);
    return newWorkout;
  }

  static async saveDeferredWorkouts(workouts: any[]): Promise<void> {
    await storage.setItem('deferredWorkouts', JSON.stringify(workouts));
  }

  static async getDeferredWorkouts(): Promise<any[]> {
    const data = await storage.getItem('deferredWorkouts');
    return JSON.parse(data || '[]');
  }

  static async clearDeferredWorkouts(): Promise<void> {
    await storage.removeItem('deferredWorkouts');
  }

  static markWorkoutMissed(workoutId: string): { success: boolean; message: string } {
    const workoutIndex = dataCache.workouts.findIndex(w => w.id === workoutId);
    if (workoutIndex === -1) {
      return { success: false, message: 'Workout not found' };
    }

    dataCache.workouts[workoutIndex] = {
      ...dataCache.workouts[workoutIndex],
      status: 'missed',
      updatedAt: new Date()
    };

    return { success: true, message: 'Workout marked as missed' };
  }

  static deleteRestDay(restDayId: string): { success: boolean; message: string } {
    const restDayIndex = dataCache.restDays.findIndex(r => r.id === restDayId);
    if (restDayIndex === -1) {
      return { success: false, message: 'Rest day not found' };
    }

    dataCache.restDays.splice(restDayIndex, 1);
    return { success: true, message: 'Rest day deleted successfully' };
  }

  static async addDeferredWorkout(workout: any): Promise<void> {
    const deferredWorkouts = await this.getDeferredWorkouts();
    deferredWorkouts.push(workout);
    await this.saveDeferredWorkouts(deferredWorkouts);
  }

  static async addPlanModification(modification: any): Promise<void> {
    // Store plan modifications in storage for now
    const data = await storage.getItem('planModifications');
    const modifications = JSON.parse(data || '[]');
    modifications.push(modification);
    await storage.setItem('planModifications', JSON.stringify(modifications));
  }

  // ========================================
  // ACTIVITY DATA METHODS
  // ========================================

  static getAllActivities(): ActivityData[] {
    return dataCache.activities || [];
  }

  static getMatchedActivity(workoutId: string): ActivityData | undefined {
    return dataCache.activities?.find(activity => activity.workout_id === workoutId);
  }

  static getStandaloneActivities(): ActivityData[] {
    return dataCache.activities?.filter(activity => !activity.workout_id) || [];
  }

  static getActivitiesInDateRange(startDate: Date, endDate: Date): ActivityData[] {
    return dataCache.activities?.filter(activity => {
      const activityDate = new Date(activity.start);
      return activityDate >= startDate && activityDate <= endDate;
    }) || [];
  }

  static getActivitiesForDate(date: Date): { workouts: Workout[]; mobility: MobilityRoutine[]; restDay?: RestDay } {
    const dateStr = date.toISOString().split('T')[0];
    
    const workouts = dataCache.workouts.filter(w => 
      w.scheduledDate && w.scheduledDate.toISOString().split('T')[0] === dateStr
    );
    
    // 🔧 CRITICAL FIX: Filter out component mobility (warmups/cooldowns) from date view
    // Component mobility should only be accessible via parent workout detail page
    const mobility = dataCache.mobilityRoutines.filter(m => 
      m.context !== 'component' &&
      m.scheduledDate && m.scheduledDate.toISOString().split('T')[0] === dateStr
    );
    
    const restDay = dataCache.restDays.find(r => 
      r.scheduledDate && r.scheduledDate.toISOString().split('T')[0] === dateStr
    );

    return { workouts, mobility, restDay };
  }

  static generateSmartPlanDescription(planId: string, goalIds: string[]): string {
    return `Smart plan description for plan ${planId} targeting goals: ${goalIds.join(', ')}`;
  }

  // ========================================
  // SESSION AND DATA MANAGEMENT
  // ========================================

  static async getWorkoutSessionHistory(workoutId: string): Promise<any[]> {
    try {
      // 🔧 JAN 27, 2026 FIX: Fetch from unified_athletic_data via backend API
      // Device-synced workouts (Garmin/Strava) are linked via workout_card_id field
      // This gives us ALL the rich data: splits, HR zones, elevation, samples, etc.
      
      console.log(`🔍 getWorkoutSessionHistory: Fetching for workout ${workoutId}`);
      
      // Fetch activity data that's linked to this workout card
      try {
        // Query unified_athletic_data WHERE workout_card_id = this workout
        // Backend will return the activity data if it exists
        const response = await authedFetch(
          `/api/workouts/${workoutId}/activity-data`
        );
        
        if (response.ok) {
          const result = await response.json();
          
          if (result.activity) {
            // Adapt to UnifiedSessionData format using SessionDataAdapter
            const { SessionDataAdapter } = await import('./sessionDataAdapter');
            const unifiedSession = SessionDataAdapter.adapt(result.activity);
            
            console.log('✅ Fetched activity data from unified_athletic_data:', unifiedSession);
            
            // Return as array (single session for this workout)
            return [{
              id: result.activity.id,
              workoutId: workoutId,
              completedAt: result.activity.start_time,
              sessionData: unifiedSession,
              activityId: result.activity.id
            }];
          }
        } else if (response.status === 404) {
          // No activity data (manual completion or not yet completed)
          console.log('ℹ️ No activity data found - checking localStorage for in-app completions');
        }
      } catch (error) {
        console.error('❌ Failed to fetch activity data from API:', error);
        // Fall through to localStorage fallback
      }
      
      // Fallback: Check localStorage for in-app tracker completions
      const allSessions = JSON.parse(await storage.getItem('workoutSessions') || '[]');
      const localSessions = allSessions.filter((session: any) => session.workoutId === workoutId)
        .sort((a: any, b: any) => new Date(b.completedAt).getTime() - new Date(a.completedAt).getTime());
      
      console.log(`📦 localStorage sessions: ${localSessions.length}`);
      return localSessions;
      
    } catch (error) {
      console.error('Error loading workout session history:', error);
      return [];
    }
  }

  static async getMobilitySessionHistory(mobilityId: string): Promise<any[]> {
    try {
      const allSessions = JSON.parse(await storage.getItem('mobilitySessions') || '[]');
      return allSessions.filter((session: any) => session.mobilityId === mobilityId)
        .sort((a: any, b: any) => new Date(b.completedAt).getTime() - new Date(a.completedAt).getTime());
    } catch (error) {
      console.error('Error loading mobility session history:', error);
      return [];
    }
  }

  static convertSessionToActivityFormat(sessionRecord: any): ActivityData {
    const sessionData = sessionRecord.sessionData;
    const baseActivity: ActivityData = {
      id: sessionRecord.id,
      external_id: `session_${sessionRecord.id}`,
      user_id: 'demo-user',
      workout_id: sessionRecord.workoutId || undefined,
      start: new Date(sessionData.startTime),
      duration_sec: sessionData.totalDuration,
      sport: sessionData.type === 'strength' ? 'strength_training' : 
             sessionData.type === 'mobility' ? 'flexibility' : 
             sessionData.type || 'workout',
      name: sessionRecord.workoutTitle || sessionRecord.mobilityTitle || 'Manual Session',
      status: 'matched',
      created_at: new Date(sessionRecord.createdAt),
      updated_at: new Date(sessionRecord.completedAt),
      samples: []
    };

    // Add specific data based on workout type
    if (sessionData.type === 'cardio') {
      baseActivity.distance_m = sessionData.distance ? sessionData.distance * 1000 : undefined;
      baseActivity.avg_hr = sessionData.heartRate?.avg;
      baseActivity.max_hr = sessionData.heartRate?.max;
      baseActivity.calories = sessionData.calories;
    } else {
      // For strength/mobility, store exercise completion data
      baseActivity.raw_summary = {
        exercisesCompleted: sessionData.exercises?.length || 0,
        exercises: sessionData.exercises || [],
        sessionType: sessionData.type,
        averageExerciseTime: sessionData.exercises?.length ? 
          sessionData.totalDuration / sessionData.exercises.length : 0
      };
    }

    return baseActivity;
  }

  // ========================================
  // UTILITY METHODS
  // ========================================

  static async getFilteredData(entityFilter: string, statusFilter: string): Promise<HierarchicalData> {
    const allData = await this.getAllData();
    
    // Filter by entity type
    let filteredData: HierarchicalData = {
      goals: entityFilter === 'goals' ? allData.goals : [],
      plans: entityFilter === 'plans' ? allData.plans : [],
      workouts: entityFilter === 'workouts' ? allData.workouts : [],
      mobilityRoutines: entityFilter === 'mobility' ? allData.mobilityRoutines : [],
      restDays: []
    };

    // Apply status filter
    if (statusFilter === 'current') {
      filteredData.goals = filteredData.goals.filter(g => g.status === 'active');
      filteredData.plans = filteredData.plans.filter(p => p.state === 'active');
      filteredData.workouts = filteredData.workouts.filter(w => w.status === 'active' || w.status === 'completed');
      filteredData.mobilityRoutines = filteredData.mobilityRoutines.filter(m => m.status === 'active' || m.status === 'completed');
    } else if (statusFilter === 'history') {
      filteredData.goals = filteredData.goals.filter(g => g.status === 'completed' || g.status === 'paused');
      filteredData.plans = filteredData.plans.filter(p => p.state === 'completed' || p.state === 'archived');
      filteredData.workouts = filteredData.workouts.filter(w => w.status === 'archived' || w.status === 'missed');
      filteredData.mobilityRoutines = filteredData.mobilityRoutines.filter(m => m.status === 'archived');
    }

    return filteredData;
  }

  /**
   * 🔄 Convert backend goal format to frontend Goal interface
   */
  private static convertBackendGoalToFrontend(backendGoal: any): Goal {
    return normalizeBackendGoal(backendGoal);
  }

  /**
   * 🔬 RESEARCH TRANSPARENCY: Fetch research event for a plan
   */
  static async getPlanResearchEvent(planId: string): Promise<any | null> {
    try {
      const response = await authedFetch(
        `/api/research/events?filter=move&event_type=plan_synthesis&plan_id=${planId}`
      );

      if (!response.ok) {
        console.error('Failed to fetch plan research event:', response.status);
        return null;
      }

      const data = await response.json();
      // Return the first event (should only be one per plan)
      return data.events && data.events.length > 0 ? data.events[0] : null;
    } catch (error) {
      console.error('Error fetching plan research event:', error);
      return null;
    }
  }

  /**
   * 🔬 RESEARCH TRANSPARENCY: Get workout research note from plan data
   */
  static getWorkoutResearchNote(workoutId: string): string | null {
    // Workouts have research_note embedded in their data
    const workout = dataCache.workouts.find(w => w.id === workoutId);
    return (workout as any)?.research_note || null;
  }

  // ========================================
  // HEART RATE ZONES
  // ========================================

  /**
   * 🆕 PHASE 3: Fetch user's heart rate zones
   * Returns zones from cache or fetches from backend
   */
  static async getUserHeartRateZones(userId: string): Promise<UserHeartRateZones | undefined> {
    // Check cache first
    if (dataCache.userHeartRateZones && dataCache.userHeartRateZones.userId === userId) {
      return dataCache.userHeartRateZones;
    }

    try {
      const response = await authedFetch(`/api/users/${userId}/hr-zones`);

      if (!response.ok) {
        console.error(`Failed to fetch HR zones: ${response.status} ${response.statusText}`);
        return undefined;
      }

      const hrZonesData = await response.json();
      if (hrZonesData) {
        const userHrZones: UserHeartRateZones = {
          ...hrZonesData,
          createdAt: new Date(hrZonesData.created_at),
          updatedAt: new Date(hrZonesData.updated_at),
        };
        dataCache.userHeartRateZones = userHrZones; // Cache the fetched zones
        return userHrZones;
      }
    } catch (error) {
      console.error('Error fetching user HR zones:', error);
    }
    return undefined;
  }

  /**
   * Get HR zones from cache (populated by fetchAllData) or fetch from /api/hr-zones.
   * Unlike getUserHeartRateZones, this doesn't require a userId parameter.
   */
  static async getCachedHeartRateZones(): Promise<UserHeartRateZones | undefined> {
    if (dataCache.userHeartRateZones) return dataCache.userHeartRateZones;
    try {
      const response = await authedFetch('/api/hr-zones');
      if (response.ok) {
        const data = await response.json();
        if (data && data.user_id) {
          const zones: UserHeartRateZones = {
            userId: data.user_id,
            source: data.source || 'cedar',
            zone1Min: data.zone_1_min, zone1Max: data.zone_1_max,
            zone2Min: data.zone_2_min, zone2Max: data.zone_2_max,
            zone3Min: data.zone_3_min, zone3Max: data.zone_3_max,
            zone4Min: data.zone_4_min, zone4Max: data.zone_4_max,
            zone5Min: data.zone_5_min, zone5Max: data.zone_5_max,
            // FE-8: model axis — row count comes from zoneCount (3/5/7).
            zone6Min: data.zone_6_min, zone6Max: data.zone_6_max,
            zone7Min: data.zone_7_min, zone7Max: data.zone_7_max,
            zoneCount: data.zone_count,
            maxHeartRate: data.max_heart_rate,
            restingHeartRate: data.resting_heart_rate,
          };
          dataCache.userHeartRateZones = zones;
          return zones;
        }
      }
    } catch { /* non-critical */ }
    return undefined;
  }

  // ========================================
  // COMPONENT MOBILITY (MISSION 22 - JAN 5, 2026)
  // ========================================

  /**
   * 🆕 PHASE 2: Get component mobility for a specific workout
   * Component mobility (warmups/cooldowns) is NOT shown in main plan view
   * but is accessible via parent workout detail page
   */
  static getComponentMobilityForWorkout(workoutId: string): MobilityRoutine[] {
    if (!dataCache.mobilityRoutines) {
      console.warn('⚠️ Mobility routines not loaded yet');
      return [];
    }

    const componentMobility = dataCache.mobilityRoutines.filter(
      m => m.workoutId === workoutId && m.context === 'component'
    );

    console.log(`🔍 Component mobility for workout ${workoutId}:`, componentMobility.length);
    return componentMobility;
  }

  /**
   * 🆕 PHASE 2: Mark mobility routine as skipped
   * Used when user chooses "Stop timer" instead of "Move onto mobility"
   */
  static async markMobilitySkipped(
    mobilityId: string, 
    skipData: {
      skip_reason: string;
      parent_workout_id?: string;
    }
  ): Promise<{ status: string; routine_id: string }> {
    try {
      const response = await authedFetch(`/api/mobility/${mobilityId}/skip`, {
        method: 'POST',
        body: JSON.stringify(skipData)
      });

      if (!response.ok) {
        throw new Error(`Failed to mark mobility as skipped: ${response.status}`);
      }

      const result = await response.json();

      // Update in cache
      const mobility = dataCache.mobilityRoutines?.find(m => m.id === mobilityId);
      if (mobility) {
        mobility.skipped = true;
        mobility.skipReason = skipData.skip_reason;
        console.log(`✅ Mobility ${mobilityId} marked as skipped in cache`);
      }

      return result;
    } catch (error) {
      console.error('❌ Error marking mobility as skipped:', error);
      throw error;
    }
  }

  // 🔄 JAN 21, 2026: DAILY ROUTINE COMPLETION METHODS

  static async getDailyRoutineCompletions(
    mobilityId: string,
    planId: string
  ): Promise<{
    monday: boolean;
    tuesday: boolean;
    wednesday: boolean;
    thursday: boolean;
    friday: boolean;
    saturday: boolean;
    sunday: boolean;
  }> {
    try {
      const response = await authedFetch(
        `/api/mobility/${mobilityId}/daily-completions?plan_id=${planId}`
      );
      
      if (!response.ok) {
        console.error('Failed to fetch daily completions:', response.status);
        // Return all false as fallback
        return {
          monday: false,
          tuesday: false,
          wednesday: false,
          thursday: false,
          friday: false,
          saturday: false,
          sunday: false
        };
      }
      
      return await response.json();
    } catch (error) {
      console.error('❌ Error fetching daily completions:', error);
      // Fallback
      return {
        monday: false,
        tuesday: false,
        wednesday: false,
        thursday: false,
        friday: false,
        saturday: false,
        sunday: false
      };
    }
  }

  static async toggleDailyRoutineCompletion(
    mobilityId: string,
    completionDate: string
  ): Promise<{ success: boolean; completed: boolean }> {
    try {
      const response = await authedFetch(
        `/api/mobility/${mobilityId}/toggle-daily-completion`,
        {
          method: 'POST',
          body: JSON.stringify({ completion_date: completionDate })
        }
      );
      
      if (!response.ok) {
        throw new Error(`Failed to toggle completion: ${response.status}`);
      }
      
      const result = await response.json();
      
      // Invalidate cache to force refresh
      dataCache.lastFetch = 0;
      
      return result;
    } catch (error) {
      console.error('❌ Error toggling daily completion:', error);
      throw error;
    }
  }

  static async getDailyRoutineAdherenceStats(
    mobilityId: string,
    weeks: number = 4
  ): Promise<{
    total_days: number;
    completed_days: number;
    adherence_percentage: number;
    weeks_analyzed: number;
  }> {
    try {
      const response = await authedFetch(
        `/api/mobility/${mobilityId}/adherence-stats?weeks=${weeks}`
      );
      
      if (!response.ok) {
        throw new Error(`Failed to fetch adherence stats: ${response.status}`);
      }
      
      return await response.json();
    } catch (error) {
      console.error('❌ Error fetching adherence stats:', error);
      throw error;
    }
  }
} 